/**
 * SummaryCard Component
 * 
 * A card component for displaying summary information or statistics.
 * 
 * @param {string} title - Card title
 * @param {string} color - Accent color: "blue", "rose", "amber", "indigo" (default: "indigo")
 * @param {React.ReactNode} children - Card content
 */

const COLOR_CLASSES = {
  blue: {
    container: "bg-blue-50 border-blue-200",
    title: "text-blue-700",
  },
  rose: {
    container: "bg-rose-50 border-rose-200",
    title: "text-rose-700",
  },
  amber: {
    container: "bg-amber-50 border-amber-200",
    title: "text-amber-700",
  },
  indigo: {
    container: "bg-indigo-50 border-indigo-200",
    title: "text-indigo-700",
  },
};

export default function SummaryCard({
  title,
  color = "indigo",
  children,
}) {
  const colors = COLOR_CLASSES[color] || COLOR_CLASSES.indigo;

  return (
    <div className={`p-4 rounded-lg border ${colors.container}`}>
      <h4 className={`${colors.title} font-medium text-sm mb-2`}>{title}</h4>
      {children}
    </div>
  );
}

/**
 * SummaryCard.Stat Component
 * 
 * A stat display for use inside SummaryCard
 * 
 * @param {string|number} value - The stat value
 * @param {string} label - Label below the value
 */
SummaryCard.Stat = function SummaryStat({ value, label }) {
  return (
    <div className="text-center">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-gray-500 text-xs">{label}</p>
    </div>
  );
};

/**
 * SummaryCard.StatGrid Component
 * 
 * A grid container for multiple stats
 * 
 * @param {number} columns - Number of columns (default: 3)
 * @param {React.ReactNode} children - Stat components
 */
SummaryCard.StatGrid = function SummaryStatGrid({ columns = 3, children }) {
  const columnClass = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
  }[columns] || "grid-cols-3";

  return (
    <div className={`grid ${columnClass} gap-4`}>
      {children}
    </div>
  );
};